/**
 * ============================================
 * USE USERS - Hooks para gestión de usuarios
 * ============================================
 *
 * Este archivo contiene hooks de TanStack Query para:
 * - Listar usuarios (useQuery)
 * - Crear usuario (useMutation)
 * - Actualizar usuario (useMutation)
 * - Eliminar usuario (useMutation)
 * - Cambiar contraseña (useMutation)
 * - Listar roles (useQuery)
 *
 * CONCEPTOS CLAVE:
 *
 * useQuery vs useMutation:
 * - useQuery: Para GET (leer datos). Se ejecuta automáticamente.
 * - useMutation: Para POST/PUT/DELETE (modificar datos). Tú decides cuándo ejecutar.
 *
 * Invalidación de cache:
 * - Después de crear/editar/eliminar, invalidamos la lista de usuarios
 * - Esto hace que TanStack Query vuelva a cargar los datos frescos
 * - El usuario ve la lista actualizada sin recargar la página
 *
 * Ejemplo de flujo:
 * 1. Usuario llena formulario y hace click en "Guardar"
 * 2. Llamamos mutate(datos)
 * 3. TanStack Query hace el POST al backend
 * 4. Si éxito: invalidamos cache → se recarga la lista
 * 5. Si error: mostramos mensaje de error
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  changePassword,
  getRoles,
} from "@/services/users";
import type { UserFilters, UserCreateData, UserUpdateData, ChangePasswordData } from "@/types/user";

// Re-export functions for useServerDataTable
export { getUsers } from "@/services/users";

// =============================================================================
// QUERIES (GET - Leer datos)
// =============================================================================

/**
 * Hook para listar usuarios con filtros y paginación
 *
 * @param filters - Filtros de búsqueda (search, role, sucursal, etc.)
 * @param options - Opciones adicionales (enabled, etc.)
 *
 * @example
 * const { data, isLoading } = useUsers({ search: "juan", page: 1 });
 */
export function useUsers(filters: UserFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    // queryKey incluye los filtros para que cada combinación tenga su propio cache
    // Ejemplo: ["users", "list", { search: "juan", page: 1 }]
    queryKey: queryKeys.users.list(filters),

    // queryFn llama al servicio existente
    queryFn: () => getUsers(filters),

    // enabled permite deshabilitar la query condicionalmente
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook para listar roles
 *
 * Los roles cambian muy poco, así que usamos un staleTime largo
 *
 * @example
 * const { data: roles, isLoading } = useRoles();
 */
export function useRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: getRoles,
    enabled: options?.enabled ?? true,
    // Los roles casi nunca cambian, cache por 30 minutos
    staleTime: 30 * 60 * 1000,
  });
}

// =============================================================================
// MUTATIONS (POST/PUT/DELETE - Modificar datos)
// =============================================================================

/**
 * Opciones comunes para callbacks de mutaciones
 */
interface MutationCallbacks {
  /** Se ejecuta cuando la mutación es exitosa */
  onSuccess?: () => void;
  /** Se ejecuta cuando hay un error */
  onError?: (error: Error) => void;
}

/**
 * Hook para crear un nuevo usuario
 *
 * useMutation retorna:
 * - mutate: Función para ejecutar la mutación
 * - mutateAsync: Versión async (retorna Promise)
 * - isPending: true mientras se ejecuta
 * - isError: true si hubo error
 * - error: El objeto de error
 * - reset: Función para limpiar el estado
 *
 * @example
 * const { mutate: createUser, isPending } = useCreateUser({
 *   onSuccess: () => toast.success("Usuario creado"),
 *   onError: (error) => toast.error(error.message),
 * });
 *
 * // Cuando el usuario hace click en guardar:
 * createUser({ email: "test@test.com", nombre: "Test", ... });
 */
export function useCreateUser(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    // mutationFn es la función que hace el POST
    mutationFn: (data: UserCreateData) => createUser(data),

    // onSuccess se ejecuta después de crear exitosamente
    onSuccess: () => {
      // Invalidar la lista de usuarios para que se recargue
      // Esto hace que cualquier componente usando useUsers() obtenga datos frescos
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });

      // Ejecutar callback del componente (ej: cerrar modal, mostrar toast)
      callbacks?.onSuccess?.();
    },

    // onError se ejecuta si hay un error
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para actualizar un usuario existente
 *
 * @example
 * const { mutate: updateUser, isPending } = useUpdateUser({
 *   onSuccess: () => toast.success("Usuario actualizado"),
 * });
 *
 * updateUser({ id: 1, data: { nombre: "Nuevo nombre" } });
 */
export function useUpdateUser(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    // La mutación recibe un objeto con id y data
    mutationFn: ({ id, data }: { id: number; data: UserUpdateData }) => updateUser(id, data),

    onSuccess: () => {
      // Invalidar lista y detalle del usuario
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      callbacks?.onSuccess?.();
    },

    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para eliminar (desactivar) un usuario
 *
 * @example
 * const { mutate: deleteUser, isPending } = useDeleteUser({
 *   onSuccess: () => toast.success("Usuario eliminado"),
 * });
 *
 * deleteUser(userId);
 */
export function useDeleteUser(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteUser(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      callbacks?.onSuccess?.();
    },

    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para activar un usuario
 *
 * @example
 * const { mutate: activateUser } = useActivateUser();
 * activateUser(userId);
 */
export function useActivateUser(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activateUser(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      callbacks?.onSuccess?.();
    },

    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para desactivar un usuario
 *
 * @example
 * const { mutate: deactivateUser } = useDeactivateUser();
 * deactivateUser(userId);
 */
export function useDeactivateUser(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateUser(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      callbacks?.onSuccess?.();
    },

    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para cambiar la contraseña de un usuario
 *
 * @example
 * const { mutate: changePassword, isPending } = useChangePassword({
 *   onSuccess: () => toast.success("Contraseña cambiada"),
 * });
 *
 * changePassword({ id: userId, data: { new_password: "newpass123" } });
 */
export function useChangePassword(callbacks?: MutationCallbacks) {
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ChangePasswordData }) =>
      changePassword(id, data),

    onSuccess: () => {
      // No necesitamos invalidar cache porque cambiar contraseña
      // no afecta los datos mostrados en la lista
      callbacks?.onSuccess?.();
    },

    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}
